import { useGetPrefectureAndMunicipalityByZipCode } from "@/api/services/global/zip-code";
import { checkDuplicateEmployeeFieldsApi, checkUpdateEmployeeInfoApi, updateEmployeeInfoApi, useEmployeeInfo } from "@/api/services/main/employee";
import { TCheckDuplicateEmployeeFieldsOutputDto } from "@/api/services/main/employee/dtos/check-duplicate-employee-fields.dto";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Form } from "@/components/elements/Form";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CheckDuplicateEmployeeFieldsModal } from "@/features/employee/components/CheckDuplicateEmployeeFieldsModal";
import { DeleteEmployeeModal } from "@/features/employee/components/DeleteEmployeeModal";
import { EmployeeForm } from "@/features/employee/components/employee-forms";
import { EmployeeFormBtnActions } from "@/features/employee/components/EmployeeFormBtnActions";
import { useGetEmployeeSchema } from "@/features/employee/hook/useGetEmployeeSchema";
import { TEmployeeForm } from "@/features/employee/types/employeeForm";
import { convertEmployeeFormDataToReqDto } from "@/features/employee/utils/convertFormDataToReqDto";
import { useDialog } from "@/hooks/useDialog";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background } from "@/theme/colors";
import { CHECK_UPDATE_EMPLOYEE_RESULT } from "@/types/enum";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { getFormValues } from "@/utils/pieces";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, styled, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TEmployeeEditPageProps = {
  previousValues: TEmployeeForm | undefined;
};

export const EmployeeEditPage: FC<TEmployeeEditPageProps> = ({ previousValues }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmMessage, setConfirmMessage] = useState<string[]>([]);
  const confirmSave = useDialog();
  const { id } = useParams();

  const { data: employeeResult } = useEmployeeInfo();

  const [existingDuplicateEmployeeFields, setExistingDuplicateEmployeeFields] = useState<TCheckDuplicateEmployeeFieldsOutputDto[]>();

  const [enableBlocker, setEnableBlocker] = useState<boolean>(true);

  const deleteModal = useVisible();

  const { employeeSchema, employeeDraftSchema } = useGetEmployeeSchema();

  const methods = useForm<TEmployeeForm>({
    defaultValues: previousValues ? previousValues : undefined,
    resolver: yupResolver(employeeSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const zipCodeWatchField = useWatch({ control: methods.control, name: "zipCode" });
  const { isFetching: isFetchingPrefectureAndMunicipality } = useGetPrefectureAndMunicipalityByZipCode(zipCodeWatchField ?? "");

  const saveDataForm = async (data: TEmployeeForm, isDraft: boolean) => {
    if (!id || isFetchingPrefectureAndMunicipality) return;
    try {
      let confirm = true;
      const payload = convertEmployeeFormDataToReqDto(data, isDraft);

      if (!!payload.residenceCode || !!payload.managementNumber || !!payload.passportCode) {
        const existing = await checkDuplicateEmployeeFieldsApi({
          employeeId: toNumber(id),
          residenceCode: payload.residenceCode,
          managementNumber: payload.managementNumber,
          passportCode: payload.passportCode,
        });

        if (existing.data.length > 0) {
          setExistingDuplicateEmployeeFields(existing.data);
          return;
        }
      }

      const checkResult = await checkUpdateEmployeeInfoApi(id, {
        employmentStatus: payload.employmentStatus,
        residenceCode: payload.residenceCode,
        residenceExpiryDate: payload.residenceExpiryDate,
        residenceStatusId: payload.residenceStatusId,
        retirementStatus: payload.retirementStatus,
        contractSpecificBusinessClassificationOneId: payload.contractSpecificBusinessClassificationOneId,
        residenceCardBackFilePath: payload.residenceCardBackFilePath,
        residenceCardFrontFilePath: payload.residenceCardFrontFilePath,
      });

      if (checkResult.data.includes(CHECK_UPDATE_EMPLOYEE_RESULT.CAN_NOT_UPDATE_RENEW_VISA)) {
        showError(new Error("期間更新申請中のため編集不可。"));
        return;
      }

      const confirmMessages: string[] = [];

      if (checkResult.data.includes(CHECK_UPDATE_EMPLOYEE_RESULT.CAN_UPDATE_AND_UPDATE_RENEW_VISA)) {
        confirmMessages.push("更新を実行すると、更新した内容が在留期間更新に反映されます。本当に更新してもよろしいですか？");
      }
      if (checkResult.data.includes(CHECK_UPDATE_EMPLOYEE_RESULT.DELETE_RENEW_VISA_AND_SHOW_CONFIRM_MODAL)) {
        confirmMessages.push("更新を実行すると、在留期間更新の申請データが削除されます。本当に更新してもよろしいですか？");
      }

      if (confirmMessages.length > 0) {
        setConfirmMessage(confirmMessages);
        confirm = await confirmSave.show();
      }

      if (!confirm) return;
      const rs = await updateEmployeeInfoApi(id, payload);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMPLOYEE.FETCH_LIST] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMPLOYEE.FETCH_DETAIL, rs.data.id] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      if (isDraft) {
        navigate(AppRoutes.employeeList);
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const onSubmit = async (data: TEmployeeForm) => {
    await saveDataForm(data, false);
  };

  const saveDraft = async () => {
    const data = methods.getValues();
    const fields = [];
    if (!data.firstName) {
      fields.push("・名");
    }
    if (!data.employmentStatus) {
      fields.push("・在籍ステータス");
    }
    if (fields.length > 0) {
      toast.error(`下書き保存のためには、以下の項目を事前にご登録ください。\n${fields.join("\n")}`);
      return;
    }

    let confirm = true;
    setConfirmMessage(["未入力項目があるため、下書き状態となり、申請は行えませんがよろしいですか？"]);
    confirm = await confirmSave.show();

    if (!confirm) return;

    try {
      setEnableBlocker(false);
      const values = await getFormValues(data, employeeDraftSchema);
      await saveDataForm(values as TEmployeeForm, true);
    } catch (error) {
      setEnableBlocker(true);
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <>
      <Form methods={methods} onSubmit={onSubmit} schema={employeeSchema} enableBlocker={enableBlocker}>
        <TopPageWrapper>
          <TopPage
            title="所属する外国人"
            isDraft={employeeResult?.data.isDraft}
            backUrl={AppRoutes.employeeList}
            rightContent={<EmployeeFormBtnActions saveDraft={saveDraft} />}
          />
        </TopPageWrapper>
        <EmployeeForm />

        <CheckDuplicateEmployeeFieldsModal
          existingDuplicateEmployeeFields={existingDuplicateEmployeeFields}
          onClose={() => setExistingDuplicateEmployeeFields(undefined)}
        />
      </Form>

      <DeleteEmployeeModal isOpen={deleteModal.visible} onClose={() => deleteModal.close()} />
      <ConfirmSaveModal open={confirmSave.open} onOk={confirmSave.confirm} onClose={confirmSave.cancel}>
        {confirmMessage.map((message, index) => (
          <Typography component="p" variant="subtitle1" key={index}>
            {message}
          </Typography>
        ))}
      </ConfirmSaveModal>
    </>
  );
};

const TopPageWrapper = styled(Box)`
  position: sticky;
  top: 80px;
  z-index: 100;
  background-color: ${background.primary};
  padding-top: 24px;
  transform: translateY(-24px);
`;
